// src/pages/PwpPaymentSuccess.jsx
// ✅ V2 — Updated to match paywall aesthetic (Fredoka One font, purple/amber theme)
// ✅ GA4 TRACKING ADDED: purchase event with revenue tracking
// ✅ META PIXEL ADDED: purchase event tracking

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

// ✅ GA4 Event Tracking
import { trackPurchase } from "../analytics/analytics";
// ✅ META PIXEL ADDED
import { pixelPurchase } from "../analytics/metaPixel";

function formatMs(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

function formatRemaining(ms) {
  if (ms == null) return "—";
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
}

async function postEntitlement(accessToken) {
  const res = await fetch("/api/pwp-entitlement", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `Entitlement failed (${res.status})`);
  }
  return json;
}

async function postVerifyPayment(accessToken, payload) {
  const res = await fetch("/api/pwp-verify-payment", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `Verify failed (${res.status})`);
  }
  return json;
}

function sanitizeNext(next) {
  if (typeof next !== "string") return "/level-2";
  if (!next.startsWith("/")) return "/level-2";
  if (next.startsWith("//")) return "/level-2";
  return next;
}

function getPriceFromPlanId(planId) {
  const prices = {
    'daily': 49,
    'weekly': 99,
    'monthly': 299
  };
  return prices[planId] || 99;
}

export default function PwpPaymentSuccess() {
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    next,
    paymentLinkId,
    paymentLinkReferenceId,
    paymentLinkStatus,
    razorpaySignature,
  } = useMemo(() => {
    const q = new URLSearchParams(search);
    const rawNext = q.get("next") || q.get("back") || "/level-2";
    return {
      next: sanitizeNext(rawNext),
      paymentLinkId:
        q.get("payment_link_id") || q.get("razorpay_payment_link_id") || null,
      paymentLinkReferenceId: q.get("payment_link_reference_id") || null,
      paymentLinkStatus: q.get("payment_link_status") || null,
      razorpaySignature: q.get("razorpay_signature") || null,
    };
  }, [search]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  const cameFromPayment = Boolean(paymentLinkId);

  useEffect(() => {
    let alive = true;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const { data: sess } = await supabase.auth.getSession();
        const session = sess?.session;

        if (!session?.access_token) {
          const backToThis = `/pwp-payment-success${search || ""}`;
          navigate(`/login?back=${encodeURIComponent(backToThis)}`, {
            replace: true,
          });
          return;
        }

        // 1) Verify payment
        if (paymentLinkId) {
          const v = await postVerifyPayment(session.access_token, {
            paymentLinkId,
            payment_link_reference_id: paymentLinkReferenceId,
            payment_link_status: paymentLinkStatus,
            razorpay_signature: razorpaySignature,
          });

          if (v?.paid === false) {
            throw new Error(
              `Payment not marked paid yet (status: ${
                v?.status || "unknown"
              }). Try again in a few seconds.`
            );
          }
        }

        // 2) Fetch latest entitlement
        const ent = await postEntitlement(session.access_token);

        if (!alive) return;

        if (!ent?.active) {
          navigate(`/pwp-paywall?next=${encodeURIComponent(next)}`, {
            replace: true,
          });
          return;
        }

        setData(ent);

        // ✅ GA4 + META PIXEL: Track purchase event
        if (cameFromPayment && !purchaseTracked && ent?.pass) {
          const transactionId = paymentLinkReferenceId || paymentLinkId || `txn_${Date.now()}`;
          const planId = ent.pass.plan_id || 'unknown';
          const amount = getPriceFromPlanId(planId);
          
          trackPurchase(transactionId, amount, planId);
          pixelPurchase(transactionId, amount, planId); // ← META PIXEL ADDED
          setPurchaseTracked(true);
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Could not load pass details.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [
    paymentLinkId,
    paymentLinkReferenceId,
    paymentLinkStatus,
    razorpaySignature,
    search,
    navigate,
    next,
    cameFromPayment,
    purchaseTracked,
  ]);

  const pass = data?.pass || null;
  const active = Boolean(data?.active);

  const expiresAtMs = pass?.expires_at ? new Date(pass.expires_at).getTime() : null;
  const purchasedAtMs = pass?.start_at ? new Date(pass.start_at).getTime() : null;
  const remainingMs = expiresAtMs ? expiresAtMs - Date.now() : null;

  const title = cameFromPayment ? "✅ Payment Successful!" : "✅ Pass Active!";
  const subtitle = cameFromPayment
    ? "Your pass is now linked to your account."
    : "Welcome back — continue your Star Quest!";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F4FF] via-white to-[#FFF7ED] font-sans flex items-center justify-center p-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');
      `}</style>

      <div className="w-full max-w-xl bg-white border-2 border-purple-200 rounded-3xl shadow-[0_8px_40px_rgba(124,58,237,0.15)] p-6 sm:p-8">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 mb-4 shadow-lg">
            <span className="text-3xl">✓</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-purple-700 mb-2"
            style={{fontFamily:"'Fredoka One',cursive"}}>
            {title}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 font-bold">{subtitle}</p>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600 mb-4"></div>
            <div className="text-slate-600 font-bold">
              {cameFromPayment ? "Finalizing your pass…" : "Checking your pass…"}
            </div>
          </div>
        )}

        {!loading && err && (
          <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-sm text-red-700 font-semibold">
            {err}
          </div>
        )}

        {!loading && !err && (
          <>
            <div className="grid gap-3 mb-6">
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wide mb-1">Status</div>
                <div className="text-lg font-extrabold" style={{fontFamily:"'Fredoka One',cursive"}}>
                  {active ? (
                    <span className="text-emerald-600">ACTIVE ✅</span>
                  ) : (
                    <span className="text-red-600">NOT ACTIVE ❌</span>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-purple-50 to-white p-4">
                <div className="text-xs text-slate-500 font-bold uppercase tracking-wide mb-1">Plan</div>
                <div className="text-lg font-extrabold text-purple-700" style={{fontFamily:"'Fredoka One',cursive"}}>
                  {pass?.plan_id?.toUpperCase() || "—"}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-wide mb-1">Activated</div>
                  <div className="text-sm font-bold text-slate-700">
                    {formatMs(purchasedAtMs)}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-wide mb-1">Expires</div>
                  <div className="text-sm font-bold text-slate-700">
                    {formatMs(expiresAtMs)}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4">
                <div className="text-xs text-amber-700 font-bold uppercase tracking-wide mb-1">Time Remaining</div>
                <div className="text-xl font-black text-amber-800" style={{fontFamily:"'Fredoka One',cursive"}}>
                  {formatRemaining(remainingMs)}
                </div>
              </div>
            </div>

            {/* Success message */}
            {cameFromPayment && (
              <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 text-center">
                <div className="text-3xl mb-2">🎉</div>
                <div className="text-sm font-extrabold text-emerald-900" style={{fontFamily:"'Fredoka One',cursive"}}>
                  All 10 songs unlocked!
                </div>
                <div className="text-xs text-emerald-700 font-semibold mt-1">
                  Your adventure begins now — collect all 8 stars!
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="space-y-3">
              <button
                onClick={() => navigate(next)}
                className="w-full rounded-xl bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED] text-white font-extrabold py-3.5 border-2 border-purple-700 shadow-[0_6px_0_rgba(88,28,135,0.35)] hover:brightness-105 active:translate-y-[2px] transition"
                style={{fontFamily:"'Fredoka One',cursive"}}
              >
                Continue Star Quest 🚀
              </button>

              <button
                onClick={() => navigate("/home")}
                className="w-full rounded-xl border-2 border-slate-200 bg-white py-3 font-bold text-slate-700 hover:bg-slate-50 transition"
              >
                Back to Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}